import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import './Shop.css';
import SiteNavBar from '../navigation/SiteNavBar';
import Footer from '../footer/Footer';
import CopyRight from '../footer/CopyRight';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';

import { config } from '../../../config';
import { formatItemsPerUnit, getItemLabel } from '../../../utils/productUnits';
import { formatProductDescription } from '../../../utils/productDescription';
const API_BASE = config.API_BASE;
console.log('API Base URL:', API_BASE);

const productCategoryParts = (item) => {
    const category = item?.category;
    if (!category) return { id: 'other', title: 'Other', detail: '' };
    if (typeof category === 'string') {
        const title = category.trim() || 'Other';
        return { id: title.toLowerCase(), title, detail: '' };
    }
    const title = category.title || 'Other';
    if (category.parentId && category.parentTitle) {
        return {
            id: String(category.parentId),
            title: category.parentTitle,
            detail: title !== category.parentTitle ? title : '',
        };
    }
    return { id: String(category.id || title), title, detail: '' };
};

const groupProductsByCategory = (products, scoreById = {}) => {
    const groups = new Map();
    products.forEach((item) => {
        const parts = productCategoryParts(item);
        if (!groups.has(parts.id)) {
            groups.set(parts.id, { id: parts.id, title: parts.title, items: [] });
        }
        groups.get(parts.id).items.push({ item, detail: parts.detail });
    });
    return Array.from(groups.values()).sort((a, b) => {
        if (a.id === 'other') return 1;
        if (b.id === 'other') return -1;
        const scoreA = Number(scoreById[a.id]) || 0;
        const scoreB = Number(scoreById[b.id]) || 0;
        if (scoreA !== scoreB) return scoreB - scoreA;
        return a.title.localeCompare(b.title);
    });
};

const categoryDisplayName = (item) => {
    const parts = productCategoryParts(item);
    if (parts.id === 'other' && !item?.category) return '';
    return parts.detail ? `${parts.title} · ${parts.detail}` : parts.title;
};

const farmNameOf = (producer) =>
    producer?.farmName ||
    producer?._doc?.farmName ||
    `${producer?.firstname || producer?._doc?.firstname || ''} ${producer?.lastname || producer?._doc?.lastname || ''}`.trim();

const productCountLabel = (count) => `${count} ${count === 1 ? 'product' : 'products'}`;

const Shop = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryScores, setCategoryScores] = useState({});
    const [attributes, setAttributes] = useState([]);
    const [producers, setProducers] = useState([]);
    const [selectedCategoryValue, setCategoryValue] = useState('');
    const [selectedAttributeIds, setSelectedAttributeIds] = useState([]);
    const [selectedProducerValue, setProducerValue] = useState('');
    const [filterProductNameValue, setFilterProductNameValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { cart, addToCart, removeFromCart } = useCart();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [orderPeriod, setOrderPeriod] = useState({ period: '', orderBy: '', distribution: '' });
    const [currentPeriodId, setCurrentPeriodId] = useState("");
    const [selectedProducer, setSelectedProducer] = useState(null);
    const [producerModalOpen, setProducerModalOpen] = useState(false);
    const [attributeDropdownOpen, setAttributeDropdownOpen] = useState(false);
    const attributeDropdownRef = useRef(null);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [expandedCategoryIds, setExpandedCategoryIds] = useState({});
    const categoryDropdownRef = useRef(null);
    const [producerDropdownOpen, setProducerDropdownOpen] = useState(false);
    const producerDropdownRef = useRef(null);

    const getCategoryParentId = (category) => {
        if (!category?.parent) return null;
        if (typeof category.parent === 'object') return category.parent._id || null;
        return category.parent;
    };

    const categoryTree = (() => {
        const byId = new Map(categories.map((c) => [c._id, c]));
        const childrenByParent = new Map();
        categories.forEach((category) => {
            const parentId = getCategoryParentId(category);
            if (!parentId || !byId.has(parentId)) return;
            const list = childrenByParent.get(parentId) || [];
            list.push(category);
            childrenByParent.set(parentId, list);
        });
        childrenByParent.forEach((list) => {
            list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        });
        return categories
            .filter((c) => {
                const parentId = getCategoryParentId(c);
                return !parentId || !byId.has(parentId);
            })
            .map((c) => ({
                ...c,
                children: childrenByParent.get(c._id) || [],
            }))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    })();

    const selectedCategoryLabel = (() => {
        if (!selectedCategoryValue) return 'All categories';
        const match = categories.find((c) => c._id === selectedCategoryValue);
        return match?.title || 'All categories';
    })();

    const selectedProducerLabel = (() => {
        if (!selectedProducerValue) return 'All producers';
        const match = producers.find((p) => p._id === selectedProducerValue);
        if (!match) return 'All producers';
        return (
            match.farmName ||
            `${match.firstname || ''} ${match.lastname || ''}`.trim() ||
            'Unknown Producer'
        );
    })();

    const categorySections = useMemo(
        () => groupProductsByCategory(items, categoryScores),
        [items, categoryScores]
    );

    const scrollToCategory = (sectionId) => {
        document.getElementById(`shop-category-${sectionId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const getCardQuantity = (productId) => {
        const cartItem = cart.find(
            (entry) => entry.id === productId || entry.productId === productId
        );
        return cartItem?.quantity || 0;
    };

    const buildCartItem = (product, quantity) => ({
        ...product,
        productId: product._id || product.id,
        id: product._id || product.id,
        quantity,
        price: product.price,
        cost: product.cost,
        producerId: product.producer?._id || product.producer,
        periodId: currentPeriodId || product.periodId,
    });

    const updateCardQuantity = (event, product, nextQuantity) => {
        event.stopPropagation();
        const inStock = (product.quantityListed || 0) > (product.quantitySold || 0);
        if (!inStock) return;
        const periodId = currentPeriodId || product.periodId;
        if (!periodId) return;

        const productId = product._id;
        const quantity = Math.max(0, Number(nextQuantity) || 0);
        const existing = cart.find(
            (entry) => entry.id === productId || entry.productId === productId
        );

        if (existing) {
            removeFromCart(productId);
        }
        if (quantity > 0) {
            addToCart(buildCartItem(product, quantity));
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (attributeDropdownRef.current && !attributeDropdownRef.current.contains(e.target)) {
                setAttributeDropdownOpen(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
                setCategoryDropdownOpen(false);
            }
            if (producerDropdownRef.current && !producerDropdownRef.current.contains(e.target)) {
                setProducerDropdownOpen(false);
            }
        };
        if (attributeDropdownOpen || categoryDropdownOpen || producerDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [attributeDropdownOpen, categoryDropdownOpen, producerDropdownOpen]);

    const fetchProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            let url = `${API_BASE}/api/products?forShop=true`;
            const params = [];
            if (selectedCategoryValue) params.push(`category=${selectedCategoryValue}`);
            selectedAttributeIds.forEach((id) => {
                if (id) params.push(`attribute=${encodeURIComponent(id)}`);
            });
            if (selectedProducerValue) params.push(`producerId=${selectedProducerValue}`);
            if (params.length > 0) url += '&' + params.join('&');
            const response = await axios.get(url);
            let filtered = response.data;
            if (filterProductNameValue) {
                filtered = filtered.filter(item => item.title && item.title.toLowerCase().includes(filterProductNameValue.toLowerCase()));
            }
            // In-stock first, then alphabetically by title
            filtered = filtered.sort((a, b) => {
                const aInStock = (a.quantityListed || 0) > (a.quantitySold || 0);
                const bInStock = (b.quantityListed || 0) > (b.quantitySold || 0);
                if (aInStock !== bInStock) {
                    return aInStock ? -1 : 1;
                }
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
            });
            setItems(filtered);
        } catch (err) {
            setError("Failed to fetch products.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/productcategories`);
            setCategories(response.data);
        } catch (err) {
            setError("Failed to fetch categories.");
        }
    };

    const fetchAttributes = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/attributes`);
            setAttributes(response.data);
        } catch (err) {
            setError("Failed to fetch attributes.");
        }
    };

    const fetchProducers = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/producers`);
            setProducers(response.data);
        } catch (err) {
            setError("Failed to fetch producers.");
        }
    };

    const fetchCart = async () => {
        // Cart is managed by CartContext, so this is just a placeholder
        // The actual cart data is handled by the useCart hook
        console.log('Cart context initialized');
    };

    // Current period id + display labels from the single backend source
    // (same endpoint the Cart uses, so all screens show identical UTC strings).
    useEffect(() => {
        axios.get(`${API_BASE}/api/orders/period`).then(res => {
            setCurrentPeriodId(res.data.periodId || "");
            setOrderPeriod({
                period: res.data.period,
                orderBy: res.data.orderBy,
                distribution: res.data.distribution
            });
        }).catch(() => {
            setCurrentPeriodId("");
            setOrderPeriod({ period: 'No current period', orderBy: 'N/A', distribution: 'N/A' });
        });
    }, []);

    useEffect(() => {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        axios.get(`${API_BASE}/api/shop/category-scores`, { headers })
            .then((res) => {
                const scores = {};
                for (const row of res.data?.categories || []) {
                    scores[String(row.id)] = Number(row.score) || 0;
                }
                setCategoryScores(scores);
            })
            .catch(() => setCategoryScores({}));
    }, [token]);

    useEffect(() => {
        // FIXED: Remove session dependency - shop doesn't need authentication
        fetchCart();
        fetchAttributes();
        fetchProducers();
        // REMOVED: Old period fetching logic - now handled in the useEffect above
    }, []);

    // FIXED: Add useEffect for fetching products and categories when filters change
    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [selectedCategoryValue, selectedAttributeIds, selectedProducerValue, filterProductNameValue]);

    return (
        <div>
            <SiteNavBar />
            <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-9 offset-lg-2 text-center">
                            <div className="hero-text">
                                <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                                    <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                                    <h1>SHOP</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`shop-catalog${modalOpen ? ' shop-catalog--modal-open' : ''}`}>
            <div className="product-filters product-filters--row">
                <div className="product-filters__field" ref={categoryDropdownRef}>
                    <span className="shop-filter-label">Category</span>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <button
                            type="button"
                            className={`form-control shop-filter-chevron shop-attribute-filter-trigger${selectedCategoryValue ? ' has-value' : ''}`}
                            onClick={() => setCategoryDropdownOpen((o) => !o)}
                            aria-expanded={categoryDropdownOpen}
                            aria-haspopup="listbox"
                        >
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: 'left',
                                }}
                            >
                                {selectedCategoryLabel}
                            </span>
                        </button>
                        {categoryDropdownOpen && (
                            <div
                                className="shop-attribute-dropdown-panel"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: '100%',
                                    marginTop: 2,
                                    zIndex: 1000,
                                    background: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: 4,
                                    maxHeight: 280,
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    padding: '8px 10px',
                                    textAlign: 'left',
                                }}
                                role="listbox"
                            >
                                <button
                                    type="button"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        background: !selectedCategoryValue ? '#f1f3f5' : 'transparent',
                                        padding: '6px 4px',
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        marginBottom: 4,
                                    }}
                                    onClick={() => {
                                        setCategoryValue('');
                                        setCategoryDropdownOpen(false);
                                    }}
                                >
                                    <span style={{ width: 8, flexShrink: 0 }} aria-hidden="true" />
                                    All categories
                                </button>
                                {categoryTree.map((category) => {
                                    const hasChildren = category.children.length > 0;
                                    const isExpanded = !!expandedCategoryIds[category._id];
                                    return (
                                        <div key={category._id}>
                                            <button
                                                type="button"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    border: 'none',
                                                    background:
                                                        selectedCategoryValue === category._id
                                                            ? '#f1f3f5'
                                                            : 'transparent',
                                                    padding: '6px 4px',
                                                    fontSize: 14,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => {
                                                    if (hasChildren) {
                                                        setExpandedCategoryIds((prev) => ({
                                                            ...prev,
                                                            [category._id]: !prev[category._id],
                                                        }));
                                                        return;
                                                    }
                                                    setCategoryValue(category._id);
                                                    setCategoryDropdownOpen(false);
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8 + 20,
                                                        flexShrink: 0,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {hasChildren ? (isExpanded ? '▾' : '▸') : null}
                                                </span>
                                                {category.title}
                                            </button>
                                            {hasChildren && isExpanded && (
                                                <div
                                                    aria-label={`${category.title} subcategories`}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    {category.children.map((child) => {
                                                        const active = selectedCategoryValue === child._id;
                                                        return (
                                                            <button
                                                                key={child._id}
                                                                type="button"
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    width: '100%',
                                                                    border: 'none',
                                                                    background: active ? '#f1f3f5' : 'transparent',
                                                                    padding: '5px 4px',
                                                                    fontSize: 13,
                                                                    textAlign: 'left',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => {
                                                                    setCategoryValue(child._id);
                                                                    setCategoryDropdownOpen(false);
                                                                }}
                                                            >
                                                                <span
                                                                    style={{ width: 8 + 2 * 20, flexShrink: 0 }}
                                                                    aria-hidden="true"
                                                                />
                                                                <span>{child.title}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                <div className="product-filters__field" ref={attributeDropdownRef}>
                    <span className="shop-filter-label">Attribute</span>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <button
                            type="button"
                            className={`form-control shop-filter-chevron shop-attribute-filter-trigger${selectedAttributeIds.length ? ' has-value' : ''}`}
                            onClick={() => setAttributeDropdownOpen((o) => !o)}
                            aria-expanded={attributeDropdownOpen}
                            aria-haspopup="listbox"
                        >
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: 'left',
                                }}
                            >
                                {selectedAttributeIds.length === 0
                                    ? 'All attributes'
                                    : selectedAttributeIds.length === 1
                                      ? attributes.find((a) => a._id === selectedAttributeIds[0])?.title || '1 selected'
                                      : `${selectedAttributeIds.length} attributes`}
                            </span>
                        </button>
                        {attributeDropdownOpen && (
                            <div
                                className="shop-attribute-dropdown-panel"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: '100%',
                                    marginTop: 2,
                                    zIndex: 1000,
                                    background: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: 4,
                                    maxHeight: 240,
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    padding: '8px 10px',
                                    textAlign: 'left',
                                }}
                                role="listbox"
                            >
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        marginBottom: 8,
                                        cursor: 'pointer',
                                        fontWeight: 'normal',
                                        fontSize: 14,
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedAttributeIds.length === 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedAttributeIds([]);
                                            }
                                        }}
                                    />
                                    <span>None</span>
                                </label>
                                <div style={{ borderTop: '1px solid #eee', paddingTop: 8 }} />
                                {attributes.map((attribute) => (
                                    <label
                                        key={attribute._id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginBottom: 6,
                                            cursor: 'pointer',
                                            fontWeight: 'normal',
                                            fontSize: 14,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedAttributeIds.includes(attribute._id)}
                                            onChange={() => {
                                                setSelectedAttributeIds((prev) =>
                                                    prev.includes(attribute._id)
                                                        ? prev.filter((id) => id !== attribute._id)
                                                        : [...prev, attribute._id]
                                                );
                                            }}
                                        />
                                        {attribute.title}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="product-filters__field" ref={producerDropdownRef}>
                    <span className="shop-filter-label">Producer</span>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <button
                            type="button"
                            className={`form-control shop-filter-chevron shop-attribute-filter-trigger${selectedProducerValue ? ' has-value' : ''}`}
                            onClick={() => setProducerDropdownOpen((o) => !o)}
                            aria-expanded={producerDropdownOpen}
                            aria-haspopup="listbox"
                        >
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: 'left',
                                }}
                            >
                                {selectedProducerLabel}
                            </span>
                        </button>
                        {producerDropdownOpen && (
                            <div
                                className="shop-attribute-dropdown-panel"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: '100%',
                                    marginTop: 2,
                                    zIndex: 1000,
                                    background: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: 4,
                                    maxHeight: 280,
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    padding: '8px 10px',
                                    textAlign: 'left',
                                }}
                                role="listbox"
                            >
                                <button
                                    type="button"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        background: !selectedProducerValue ? '#f1f3f5' : 'transparent',
                                        padding: '6px 4px',
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        marginBottom: 4,
                                    }}
                                    onClick={() => {
                                        setProducerValue('');
                                        setProducerDropdownOpen(false);
                                    }}
                                >
                                    <span style={{ width: 8, flexShrink: 0 }} aria-hidden="true" />
                                    All producers
                                </button>
                                {producers.map((producer) => {
                                    const name =
                                        producer.farmName ||
                                        `${producer.firstname || ''} ${producer.lastname || ''}`.trim() ||
                                        'Unknown Producer';
                                    const active = selectedProducerValue === producer._id;
                                    return (
                                        <button
                                            key={producer._id}
                                            type="button"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                textAlign: 'left',
                                                border: 'none',
                                                background: active ? '#f1f3f5' : 'transparent',
                                                padding: '6px 4px',
                                                fontSize: 14,
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                setProducerValue(producer._id);
                                                setProducerDropdownOpen(false);
                                            }}
                                        >
                                            <span
                                                style={{ width: 8 + 20, flexShrink: 0 }}
                                                aria-hidden="true"
                                            />
                                            {name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                <div className="product-filters__field">
                    <span className="shop-filter-label">Product</span>
                    <input
                        type="text"
                        placeholder="Search by name"
                        className="form-control"
                        value={filterProductNameValue}
                        onChange={(e) => setFilterProductNameValue(e.target.value)}
                    />
                </div>
            </div>
            {loading ? (
                <p className="shop-status">Loading products...</p>
            ) : error ? (
                <p className="shop-status error">{error}</p>
            ) : items.length === 0 ? (
                <p className="shop-status">No products found.</p>
            ) : (
                <>
                    {categorySections.length > 1 && (
                        <nav className="shop-category-nav" aria-label="Shop categories">
                            {categorySections.map((section) => (
                                <button
                                    key={section.id}
                                    type="button"
                                    onClick={() => scrollToCategory(section.id)}
                                >
                                    {section.title}
                                    <span>{section.items.length}</span>
                                </button>
                            ))}
                        </nav>
                    )}
                    {categorySections.map((section) => (
                        <section
                            key={section.id}
                            id={`shop-category-${section.id}`}
                            className="shop-category"
                            aria-labelledby={`shop-category-title-${section.id}`}
                        >
                            <header className="shop-category__header">
                                <h2 id={`shop-category-title-${section.id}`} className="shop-category__title">
                                    {section.title}
                                </h2>
                                <p className="shop-category__count">{productCountLabel(section.items.length)}</p>
                            </header>
                            <div className="product-grid">
                                {section.items.map(({ item }) => {
                                    const inStock = (item.quantityListed || 0) > (item.quantitySold || 0);
                                    const showItemsPerUnit = Number(item.unitcount) >= 1 && item.unit;
                                    const cardQuantity = getCardQuantity(item._id);
                                    const farmName = farmNameOf(item.producer);
                                    return (
                                        <div
                                            className={`itemBox${inStock ? '' : ' itemBox--soldout'}`}
                                            key={item._id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                setSelectedProduct(item);
                                                setModalOpen(true);
                                                setQuantity(Math.max(1, cardQuantity));
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                                event.preventDefault();
                                                setSelectedProduct(item);
                                                setModalOpen(true);
                                                setQuantity(Math.max(1, cardQuantity));
                                            }}
                                        >
                                            {cardQuantity > 0 && (
                                                <span className="shop-card-qty-badge" aria-label={`${cardQuantity} in cart`}>
                                                    {cardQuantity}
                                                </span>
                                            )}
                                            <div className="item-picture-slider">
                                                {item.media?.[0] ? (
                                                    <img className="item-picture-slider-img--static" alt={item.title} src={item.media[0]} />
                                                ) : (
                                                    <div className="shop-card-image-fallback" aria-hidden="true" />
                                                )}
                                            </div>
                                            <div className="shop-card-body">
                                                {farmName && <p className="shop-card-farm">{farmName}</p>}
                                                <h3 className="item-box-title">{item.title}</h3>
                                                <div className="shop-card-price">
                                                    <span>${Number(item.cost).toFixed(2)}</span>
                                                    {item.unit && <span className="shop-card-price__unit"> / {item.unit}</span>}
                                                </div>
                                                {showItemsPerUnit && (
                                                    <p className="shop-card-pack">
                                                        {item.unitcount} {getItemLabel(item.unitcount)} per {item.unit}
                                                    </p>
                                                )}
                                                <p className={`shop-stock-pill${inStock ? ' is-in' : ' is-out'}`}>
                                                    {inStock ? 'In stock' : 'Out of stock'}
                                                </p>
                                            </div>
                                            <div
                                                className="shop-card-add-controls"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                {cardQuantity > 0 && (
                                                    <button
                                                        type="button"
                                                        className="shop-card-add-btn shop-card-add-btn--minus"
                                                        onClick={(event) => updateCardQuantity(event, item, cardQuantity - 1)}
                                                        disabled={!inStock}
                                                        aria-label={`Decrease quantity of ${item.title}`}
                                                    >
                                                        -
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="shop-card-add-btn shop-card-add-btn--plus"
                                                    onClick={(event) => updateCardQuantity(event, item, cardQuantity + 1)}
                                                    disabled={!inStock}
                                                    aria-label={`Add ${item.title} to cart`}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                </>
            )}
            </div>
            <Modal
                isOpen={modalOpen}
                onRequestClose={() => setModalOpen(false)}
                contentLabel="Product Details"
                ariaHideApp={false}
                style={{
                    overlay: {
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                    content: {
                        maxWidth: 800,
                        margin: 'auto',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 0,
                        zIndex: 10000,
                        border: 'none',
                        borderRadius: 16,
                        overflow: 'hidden',
                    },
                }}
            >
                {selectedProduct && (() => {
                    const inStock = (selectedProduct.quantityListed || 0) > (selectedProduct.quantitySold || 0);
                    const hasPeriod = Boolean(currentPeriodId || selectedProduct.periodId);
                    const canAdd = inStock && hasPeriod;
                    const itemsPerUnit = formatItemsPerUnit(selectedProduct.unitcount, selectedProduct.unit);
                    return (
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div style={{ flex: 1, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 350 }}>
                                <img src={selectedProduct.media[0]} alt={selectedProduct.title} style={{ maxWidth: 300, maxHeight: 300, objectFit: 'contain' }} />
                            </div>
                            <div style={{ flex: 2, padding: 32, background: '#fff', minHeight: 350, position: 'relative' }}>
                                <button onClick={() => setModalOpen(false)} style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
                                <h2 style={{ marginBottom: 8 }}>{selectedProduct.title}</h2>
                                {categoryDisplayName(selectedProduct) && (
                                    <p className="shop-modal-category">{categoryDisplayName(selectedProduct)}</p>
                                )}
                                <div 
                                    style={{ color: '#007bff', marginBottom: 8, cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => {
                                        // Set the selected producer and open producer modal
                                        setSelectedProducer(selectedProduct.producer);
                                        setProducerModalOpen(true);
                                    }}
                                >
                                    {selectedProduct.producer?.farmName || `${selectedProduct.producer?.firstname || ''} ${selectedProduct.producer?.lastname || ''}`}
                                </div>
                                <div
                                    className="product-description product-description--scrollable"
                                    dangerouslySetInnerHTML={formatProductDescription(selectedProduct.description)}
                                />
                                <div style={{ marginBottom: 16, fontWeight: 'bold' }}>SHOP NOW</div>
                                <div style={{ marginBottom: 8 }}>
                                    <b>Order Period:</b> {orderPeriod.period || 'N/A'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <b>Order By:</b> {orderPeriod.orderBy || 'N/A'}
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <b>Distribution Date:</b> {orderPeriod.distribution || 'N/A'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                    {itemsPerUnit && (
                                        <span style={{ fontWeight: 'bold', marginRight: 8 }}>{itemsPerUnit}</span>
                                    )}
                                    <span style={{ fontWeight: 'bold', marginRight: 8 }}>${Number(selectedProduct.cost).toFixed(2)}</span>
                                    <div className="shop-qty-picker shop-qty-picker--inline">
                                        <button
                                            type="button"
                                            className="shop-qty-btn"
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            disabled={!canAdd}
                                        >
                                            -
                                        </button>
                                        <input
                                            type="text"
                                            className="shop-qty-input"
                                            value={quantity}
                                            onChange={e => {
                                                const value = e.target.value;
                                                if (value === '' || (!isNaN(value) && Number(value) >= 1)) {
                                                    setQuantity(value === '' ? 1 : Number(value));
                                                }
                                            }}
                                            onKeyDown={e => {
                                                if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                                                    (e.keyCode === 65 && e.ctrlKey === true) ||
                                                    (e.keyCode === 67 && e.ctrlKey === true) ||
                                                    (e.keyCode === 86 && e.ctrlKey === true) ||
                                                    (e.keyCode === 88 && e.ctrlKey === true) ||
                                                    (e.keyCode >= 35 && e.keyCode <= 39)) {
                                                    return;
                                                }
                                                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            disabled={!canAdd}
                                        />
                                        <button
                                            type="button"
                                            className="shop-qty-btn"
                                            onClick={() => setQuantity(quantity + 1)}
                                            disabled={!canAdd}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <div style={{ width: 15 }}></div>
                                    <button className="cart-btn" onClick={() => {
                                        if (!canAdd) return;
                                        addToCart(buildCartItem(selectedProduct, quantity));
                                        setModalOpen(false);
                                    }} disabled={!canAdd}>
                                        ADD
                                    </button>
                                </div>
                                <div style={{ marginTop: 8, fontWeight: 'bold', color: inStock ? 'green' : 'red' }}>
                                    {inStock ? 'In Stock' : 'Out of Stock'}
                                </div>
                                {!inStock && <div style={{ color: 'red', marginTop: 8 }}>This item is out of stock and cannot be added to your cart.</div>}
                                {inStock && !hasPeriod && <div style={{ color: 'red', marginTop: 8 }}>No current order period. This item cannot be added to your cart yet.</div>}
                            </div>
                        </div>
                    );
                })()}
            </Modal>
            
            {/* Producer Modal */}
            <Modal
                isOpen={producerModalOpen}
                onRequestClose={() => setProducerModalOpen(false)}
                contentLabel="Producer Details"
                ariaHideApp={false}
                style={{ 
                    overlay: {
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        paddingTop: '120px'
                    },
                    content: { 
                        maxWidth: 900, 
                        maxHeight: 'calc(90vh - 140px)',
                        margin: 'auto', 
                        borderRadius: 16, 
                        padding: 0,
                        overflow: 'auto',
                        zIndex: 10000,
                        position: 'relative',
                        top: 0
                    } 
                }}
            >
                {selectedProducer && (
                    <div style={{ display: 'flex', flexDirection: 'row', minHeight: 400, position: 'relative' }}>
                        <button 
                            onClick={() => setProducerModalOpen(false)} 
                            style={{ 
                                position: 'absolute', 
                                top: 16, 
                                right: 16, 
                                fontSize: 24, 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                zIndex: 10001
                            }}
                        >
                            &times;
                        </button>
                        <div style={{ flex: 1, background: '#fafafa', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img src={selectedProducer.logo || '/default-producer.png'} alt={selectedProducer.farmName} style={{ width: 140, height: 140, borderRadius: '50%', objectFit: 'cover', marginBottom: 16, border: '2px solid #eee' }} />
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                                {selectedProducer.gallery && selectedProducer.gallery.map((img, idx) => (
                                    <img key={idx} src={img} alt="Gallery" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />
                                ))}
                            </div>
                        </div>
                        <div style={{ flex: 2, padding: 32, background: '#fff', minHeight: 400, overflow: 'auto' }}>
                            <h2 style={{ marginBottom: 8, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{selectedProducer.farmName}</h2>
                            <div style={{ color: '#888', marginBottom: 8, wordWrap: 'break-word' }}>{selectedProducer.address?.city}, {selectedProducer.address?.state}</div>
                            <div style={{ marginBottom: 8 }}><b>Contact:</b> {selectedProducer.name || selectedProducer.contactName || 'N/A'}</div>
                            <div style={{ marginBottom: 8 }}><b>Address:</b> {selectedProducer.address?.street}, {selectedProducer.address?.city}, {selectedProducer.address?.state} {selectedProducer.address?.zip}</div>
                            <div style={{ marginBottom: 16 }}><b>Phone:</b> {selectedProducer.contactNumber || 'N/A'}</div>
                            <div style={{ marginBottom: 16, wordWrap: 'break-word', overflowWrap: 'break-word' }}><b>About Us:</b><br />{selectedProducer.aboutUs || selectedProducer.description || 'No about us info.'}</div>
                            <div style={{ marginBottom: 16, wordWrap: 'break-word', overflowWrap: 'break-word' }}><b>Practices:</b><br />{selectedProducer.practices || 'No practices info.'}</div>
                            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                                <a
                                    href={`mailto:team@farmersmarketspace.com?subject=Connect with Producer`}
                                    style={{
                                        background: '#007bff',
                                        color: '#fff',
                                        padding: '8px 16px',
                                        borderRadius: 8,
                                        textDecoration: 'none',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Connect
                                </a>
                                <a
                                    href={`mailto:team@farmersmarketspace.com?subject=Schedule Visit`}
                                    style={{
                                        background: '#28a745',
                                        color: '#fff',
                                        padding: '8px 16px',
                                        borderRadius: 8,
                                        textDecoration: 'none',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Schedule Visit
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
            
            <Footer />
            <CopyRight />
        </div>
    );
};

export default Shop;